import {useNavigate} from 'react-router-dom';

import {HealthBookingEnum, QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';

import {UseChangeFlowDialogProps} from './types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {getChangeFlowDialogDescription} from '../../utils/getChangeFlowDialogDescription';
import {LOCATION_ROUTE} from 'presentation/modules/booking/shared/constants/bookingLocationPath';
import {ChangeFlowVariantEnum} from '../../constants';
import {useResetBookingFlow} from 'presentation/modules/booking/shared/hooks/useResetBookingFlow';
import {useIsAdvanceDiagnosticsExamsJourney} from 'presentation/modules/booking/exam/hooks/useIsAdvanceDiagnosticsExamsJourney';

export function useChangeFlowDialog({
	flowType,
	variant,
	setActiveBookingFlowStep,
	setOpen,
}: UseChangeFlowDialogProps) {
	const {isManualLocation, isFavoriteDoctorsFlow} = useBookingStore(state => state.bookingData);
	const isAdvanceDiagnosticsFlow = useIsAdvanceDiagnosticsExamsJourney();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isManualLocationHealthExamOdonto =
		isManualLocation && (isHealthFlow || isExamFlow || isOdontoFlow);
	const {LOCATION_STEP, SPECIALITY_OR_EXAM_STEP, UNIT_STEP} = getStepPosition({
		flowType,
		isManualLocation,
	});

	const {setSearchParams, clearSearch} = useQueryParams();
	const navigate = useNavigate();

	const description = getChangeFlowDialogDescription({variant, flowType}).description;
	const {resetBookingFlow} = useResetBookingFlow();

	const handleClose = () => {
		setOpen(false);
	};

	const handleConfirm = () => {
		setOpen(false);
		clearSearch();

		if (variant === ChangeFlowVariantEnum.LOCATION && setActiveBookingFlowStep) {
			resetBookingFlow();

			if (isManualLocationHealthExamOdonto) {
				setActiveBookingFlowStep(LOCATION_STEP);
				return;
			}
			navigate(LOCATION_ROUTE[flowType], {replace: true});
			setActiveBookingFlowStep(LOCATION_STEP);
			return;
		}

		if (variant === ChangeFlowVariantEnum.SPECIALTY && setActiveBookingFlowStep) {
			resetBookingFlow({
				keepGeolocationInfo: true,
				keepTypeLocalName: true,
				keepAdvanceDiagnosticsContext: isExamFlow && isAdvanceDiagnosticsFlow,
			});

			setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
			return;
		}

		if (variant === ChangeFlowVariantEnum.UNIT && setActiveBookingFlowStep) {
			setActiveBookingFlowStep(UNIT_STEP);

			setBookingData(prev => ({
				...prev,
				professionalCode: isFavoriteDoctorsFlow ? prev.professionalCode : '',
				professionalName: isFavoriteDoctorsFlow ? prev.professionalName : '',
				dateAt: '',
				hourAt: '',
				unitCode: '',
				unitName: '',
				unitAddress: '',
				odontoRecommendations: '',
				districtName: isManualLocationHealthExamOdonto ? prev.districtName : '',
				odontoUnitDistrictSelected: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.SEARCH_QUERY);
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
					prev.delete(QueryParamsEnum.DISABILITY);
					prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);

					return prev;
				},
				{
					replace: true,
				},
			);

			if (flowType === FlowEnum.TELEHEALTH) {
				navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
			}
		}
	};

	return {
		description,
		handleConfirm,
		handleClose,
	};
}
